import { api, ApiError } from './client.js';

export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  changePassword: (body) => api.post('/auth/change-password', body),
  updatePreferences: (body) => api.patch('/auth/preferences', body),
};

export const metaApi = {
  get: () => api.get('/meta'),
  health: () => fetch('/health').then((r) => r.json()),
};

export const dashboardApi = {
  get: () => api.get('/dashboard'),
};

export const customerApi = {
  list: (query) => api.get('/customers', { query }),
  get: (id, locale) => api.get(`/customers/${id}`, { query: { locale } }),
  create: (body) => api.post('/customers', body),
  update: (id, body) => api.patch(`/customers/${id}`, body),
  assign: (id, body) => api.patch(`/customers/${id}/assign`, body),
  // Marking the deal signed releases the account to the IT team - or takes it
  // back out again.
  setSigned: (id, signed) => api.patch(`/customers/${id}/signature`, { signed }),
  remove: (id) => api.delete(`/customers/${id}`),
};

export const fileApi = {
  /** Raw upload: the browser sends the bytes, not a multipart form. */
  upload: async (file) => {
    const token = localStorage.getItem('crm.token');
    const response = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/files`, {
      method: 'POST',
      headers: {
        'Content-Type': file.type,
        'X-Filename': file.name,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: file,
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok) throw new ApiError(response.status, payload?.error?.code ?? 'INTERNAL_ERROR', payload?.error?.message ?? 'Upload failed');
    return payload;
  },
  /**
   * Only good for a PUBLIC file - the branding logo, which <img src> fetches
   * without a header. Everything else is private; use open() below.
   */
  url: (id) => (id ? `${import.meta.env.VITE_API_URL || ''}/api/files/${id}` : null),

  /**
   * The bytes of a private file, fetched with the token.
   *
   * Everything the client does with an attachment - previewing it, downloading
   * it - starts here, because `/api/files/:id` needs an Authorization header
   * and neither <img src> nor <a href> can send one. Putting the token in the
   * URL instead would leak it into logs and referrers.
   */
  blob: async (id) => {
    const token = localStorage.getItem('crm.token');
    const response = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/files/${id}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new ApiError(
        response.status,
        payload?.error?.code ?? 'INTERNAL_ERROR',
        payload?.error?.message ?? 'Download failed',
      );
    }
    return response.blob();
  },

  /** Save it to disk, under the name it was sent with. */
  download: async (id, filename) => {
    const url = URL.createObjectURL(await fileApi.blob(id));
    const link = document.createElement('a');
    link.href = url;
    link.rel = 'noopener';
    if (filename) link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Long enough for the browser to have taken the bytes.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
};

export const leadApi = {
  list: (query) => api.get('/leads', { query }),
  get: (id) => api.get(`/leads/${id}`),
  create: (body) => api.post('/leads', body),
  update: (id, body) => api.patch(`/leads/${id}`, body),
  route: (id, commercialId) => api.post(`/leads/${id}/route`, { commercialId }),
  brief: (id, locale) => api.get(`/leads/${id}/brief`, { query: { locale } }),
};

export const documentApi = {
  list: (query) => api.get('/documents', { query }),
  get: (id) => api.get(`/documents/${id}`),
  create: (body) => api.post('/documents', body),
  update: (id, body) => api.patch(`/documents/${id}`, body),
  convert: (id, type) => api.post(`/documents/${id}/convert`, { type }),
  payment: (id, amount) => api.post(`/documents/${id}/payment`, { amount }),
  // Chases an unpaid invoice: no email is sent - it records the chase and
  // raises a dated follow-up. See remind() in the API.
  remind: (id) => api.post(`/documents/${id}/remind`),
  // Emails the document to the customer. Refused unless SMTP is configured AND
  // an administrator has switched Integrations -> Mailbox on.
  send: (id, body) => api.post(`/documents/${id}/send`, body),
  /** Freeze and archive it without sending - the Issue button. */
  issue: (id) => api.post(`/documents/${id}/issue`),
  /** The only lawful correction: credit note, then a new invoice to fix. */
  cancel: (id, body) => api.post(`/documents/${id}/cancel`, body),
  archive: (id) => api.get(`/documents/${id}/archive`),
  /** The PDF the customer receives. Private, so it goes through the token. */
  pdf: async (id, filename) => {
    const token = localStorage.getItem('crm.token');
    const response = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/documents/${id}/pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new ApiError(response.status, payload?.error?.code ?? 'INTERNAL_ERROR', payload?.error?.message ?? 'Download failed');
    }
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener';
    if (filename) link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
  remove: (id) => api.delete(`/documents/${id}`),
};

export const productApi = {
  list: (query) => api.get('/products', { query }),
  create: (body) => api.post('/products', body),
  update: (id, body) => api.patch(`/products/${id}`, body),
  remove: (id) => api.delete(`/products/${id}`),
};

export const commentApi = {
  list: (query) => api.get('/comments', { query }),
  create: (body) => api.post('/comments', body),
  update: (id, body) => api.patch(`/comments/${id}`, body),
  remove: (id) => api.delete(`/comments/${id}`),
};

export const importApi = {
  // dryRun asks the server what WOULD happen - the preview shown before saving.
  prospects: (csv, dryRun = false) => api.post('/customers/import', { csv, dryRun }),
  sample: () => api.text('/customers/import/sample'),
};

export const chatApi = {
  conversations: () => api.get('/chat/conversations'),
  // One id opens a direct room, several open a group.
  open: (userIds, title = '') => api.post('/chat/conversations', { userIds, title }),
  messages: (id, query) => api.get(`/chat/conversations/${id}/messages`, { query }),
  send: (id, body, attachmentIds = []) =>
    api.post(`/chat/conversations/${id}/messages`, { body, attachmentIds }),
  markRead: (id) => api.post(`/chat/conversations/${id}/read`),
};

export const notificationApi = {
  list: (query) => api.get('/notifications', { query }),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
};

export const taskApi = {
  list: (query) => api.get('/tasks', { query }),
  create: (body) => api.post('/tasks', body),
  update: (id, body) => api.patch(`/tasks/${id}`, body),
  remove: (id) => api.delete(`/tasks/${id}`),
};

export const projectApi = {
  list: (query) => api.get('/projects', { query }),
  get: (id) => api.get(`/projects/${id}`),
  create: (body) => api.post('/projects', body),
  update: (id, body) => api.patch(`/projects/${id}`, body),
  remove: (id) => api.delete(`/projects/${id}`),
};

export const agencyApi = {
  list: (query) => api.get('/agencies', { query }),
  get: (id) => api.get(`/agencies/${id}`),
  members: (query) => api.get('/agencies/members', { query }),
  create: (body) => api.post('/agencies', body),
  update: (id, body) => api.patch(`/agencies/${id}`, body),
  disable: (id) => api.post(`/agencies/${id}/disable`),
};

export const statsApi = {
  get: (query) => api.get('/stats', { query }),
};

export const settingsApi = {
  get: () => api.get('/settings'),
  update: (body) => api.put('/settings', body),
  reset: () => api.post('/settings/reset'),
};

export const questionnaireApi = {
  telepro: (locale) => api.get('/questionnaires/telepro', { query: { locale } }),
  audit: () => api.get('/questionnaires/telepro/audit'),
  score: (answers) => api.post('/questionnaires/telepro/score', { answers }),
};

export const agendaApi = {
  commercials: () => api.get('/agenda/commercials'),
  availability: (userId) => api.get(`/agenda/availability/${userId}`),
  setAvailability: (userId, body) => api.put(`/agenda/availability/${userId}`, body),
  addBlock: (userId, body) => api.post(`/agenda/availability/${userId}/blocks`, body),
  removeBlock: (userId, blockId) => api.delete(`/agenda/availability/${userId}/blocks/${blockId}`),
  slots: (query) => api.get('/agenda/slots', { query }),
  appointments: (query) => api.get('/agenda/appointments', { query }),
  appointment: (id) => api.get(`/agenda/appointments/${id}`),
  book: (body) => api.post('/agenda/appointments', body),
  update: (id, body) => api.patch(`/agenda/appointments/${id}`, body),
  cancel: (id, reason) => api.post(`/agenda/appointments/${id}/cancel`, { reason }),
};

export const userApi = {
  list: (query) => api.get('/users', { query }),
  directory: () => api.get('/users/directory'),
  get: (id) => api.get(`/users/${id}`),
  create: (body) => api.post('/users', body),
  update: (id, body) => api.patch(`/users/${id}`, body),
  resetPassword: (id, body) => api.post(`/users/${id}/reset-password`, body),
  disable: (id) => api.post(`/users/${id}/disable`),
};

export const activityApi = {
  list: (query) => api.get('/activities', { query }),
};
